import {
  Blockchain,
  ConnectWalletError,
  isConnectWalletError,
  useGetApproved,
  useOwnerOf721,
  useIsApprovedForAll,
  useSafeTransferFrom721,
} from '@verisart/nft/src'
import { useEffect, useState } from 'react'
import Notification from './Notification'
import t, { formatString } from 'src/lib/translation'
import Dialog from '../atoms/Dialog'
import Button from '../atoms/Button'
import { Spinner } from '@verisart/shared'
import Tick from '@verisart/shared/src/icons/tick-black'
import {
  trackNftTransferButtonClicked,
  trackNftTransferCompleted,
  trackNftTransferStarted,
} from 'src/lib/mixpanel'
import SquareButton from '../atoms/SquareButton'
import { useConnectWallet } from '@verisart/nft/src'
import { Address } from 'viem'
import { useAccount, useAccountEffect } from 'wagmi'

const TransferNFT: React.FC<{
  blockchain: Blockchain
  contractAddress: string
  tokenId: string
}> = ({ blockchain, contractAddress, tokenId }) => {
  const [showTransferModal, setShowTransferModal] = useState(false)
  const [triggerTransfer, setTriggerTransfer] = useState(false)
  const [transferAddress, setTransferAddress] = useState('')
  const [ownerError, setOwnerError] = useState<string | undefined>(undefined)
  const [transferProcessStarted, setTransferProcessStarted] = useState(false)
  const [transferInProgress, setTransferInProgress] = useState(false)
  const [transferComplete, setTransferComplete] = useState(false)
  const [transactionError, setTransactionError] = useState<string | undefined>(
    undefined
  )

  const { connect, disconnect, ensureBlockchain } = useConnectWallet()

  const [connectError, setConnectError] = useState<
    ConnectWalletError | undefined | string
  >(undefined)

  const { address: activeWallet, connector } = useAccount()
  const { data: approvedWallet } = useGetApproved(
    contractAddress as Address,
    tokenId
  )
  const { data: owner } = useOwnerOf721(contractAddress as Address, tokenId)
  const { data: isApproved } = useIsApprovedForAll(
    contractAddress as Address,
    owner,
    activeWallet
  )

  const { mutateAsync: safeTransferFrom721 } = useSafeTransferFrom721()

  useEffect(() => {
    const checkCanTransfer = async () => {
      try {
        if (showTransferModal) {
          await ensureBlockchain(blockchain)
          if (
            activeWallet?.toLowerCase() === owner?.toLowerCase() ||
            approvedWallet?.toLowerCase() === activeWallet?.toLowerCase() ||
            isApproved === true
          ) {
            setOwnerError(undefined)
          } else {
            setOwnerError('Connected Wallet does not own the NFT')
          }
        }
      } catch (e) {
        const error: any = new Error(
          `Error checking if wallet can transfer NFT`
        )
        error.cause = e
        throw error
      }
    }

    checkCanTransfer()
  }, [
    contractAddress,
    tokenId,
    activeWallet,
    blockchain,
    showTransferModal,
    ensureBlockchain,
    owner,
    approvedWallet,
    isApproved,
  ])

  const transferNFT = async () => {
    if (activeWallet) {
      try {
        setTransferProcessStarted(true)
        setTransactionError(undefined)
        await ensureBlockchain(blockchain)
        setTransferProcessStarted(false)
        trackNftTransferStarted({
          walletType: connector?.id,
          fromAddress: activeWallet,
          toAddress: transferAddress,
        })
        setTransferInProgress(true)
        await safeTransferFrom721({
          contractAddress: contractAddress as Address,
          tokenId,
          toAddress: transferAddress as Address,
        })
        setTransferInProgress(false)
        setTransferComplete(true)
        trackNftTransferCompleted({
          walletType: connector?.id,
          fromAddress: activeWallet,
          toAddress: transferAddress,
        })
      } catch (e) {
        setTransferProcessStarted(false)
        setTransferInProgress(false)
        if (e.message.includes('invalid address')) {
          setTransactionError('Invalid address: ' + transferAddress)
        } else if (e.message.includes('User denied transaction signature')) {
          setTransactionError('User cancelled transaction')
        } else {
          setTransactionError('Please contact support at support@verisart.com')
        }
        throw new Error(e)
      }
    }
  }

  const resetStates = () => {
    setShowTransferModal(false)
    setConnectError(undefined)
    setOwnerError(undefined)
    setTransferAddress('')
    setTransactionError(undefined)
    setTransferInProgress(false)
    setTransferComplete(false)
    setTransferProcessStarted(false)
  }

  useAccountEffect({
    onConnect: async ({ connector, address }) => {
      if (!triggerTransfer) {
        return
      }
      try {
        await ensureBlockchain(blockchain)
        setShowTransferModal(true)
        trackNftTransferButtonClicked({
          walletType: connector.id,
          fromAddress: address,
        })
      } catch (e) {
        setConnectError(e)
      } finally {
        setTriggerTransfer(false)
      }
    },
  })

  return (
    <div>
      <SquareButton
        title={t.works.transferNFT}
        onClick={async () => {
          setTriggerTransfer(true)
          if (!activeWallet) {
            await connect()
          } else {
            try {
              await ensureBlockchain(blockchain)
              setShowTransferModal(true)
              trackNftTransferButtonClicked({
                walletType: connector?.id,
                fromAddress: activeWallet,
              })
            } catch (e) {
              setConnectError(e)
            } finally {
              setTriggerTransfer(false)
            }
          }
        }}
      />
      {showTransferModal && (
        <Dialog
          show={true}
          onDismiss={() => {
            resetStates()
          }}
          showCloseButton
          disableBackgroundClose={true}
        >
          <div className="ver-min-w-[50rem]">
            {transactionError && (
              <div className="ver-pb-4 ver-max-w-5xl">
                <Notification
                  body={transactionError}
                  style="warning"
                  additionalClasses={'ver-mt-5'}
                />
              </div>
            )}
            {connectError && !activeWallet && (
              <div className="ver-pb-4">
                <Notification
                  title={t.claim.notification.title.connectError}
                  body={formatString(
                    isConnectWalletError(connectError)
                      ? connectError.message
                      : connectError
                  )}
                  style="error"
                  additionalClasses={'ver-mt-5'}
                />
              </div>
            )}
            {ownerError && (
              <div className="ver-pb-4 ver-max-w-5xl">
                <Notification
                  body={ownerError}
                  style="error"
                  additionalClasses={'ver-mt-5'}
                />
              </div>
            )}
            {(!ownerError || !connectError) && (
              <>
                <p className="ver-pb-4">{t.works.transferNFTHelpText}</p>
                <div className="ver-items-center">
                  <input
                    disabled={!!ownerError || transferInProgress}
                    type="text"
                    value={transferAddress}
                    pattern={'^0x[a-fA-F0-9]{40}$'}
                    onChange={(e) => setTransferAddress(e.target.value)}
                    placeholder="Wallet Address"
                    style={{
                      width: '100%',
                      border: '1px solid #ccc',
                      padding: '10px',
                    }}
                  />
                </div>
              </>
            )}
            {transferProcessStarted && (
              <>
                <div className="ver-flex ver-pt-4">
                  <Spinner />
                  <p className="ver-pl-5">
                    {t.works.transferNFTProcessStarted}
                  </p>
                </div>
              </>
            )}
            {transferInProgress && (
              <>
                <div className="ver-flex ver-pt-4">
                  <Spinner />
                  <p className="ver-pl-5">{t.works.transferNFTInProgress}</p>
                </div>
              </>
            )}
            {transferComplete && (
              <>
                <div className="ver-flex ver-pt-4">
                  <Tick />
                  <p className="ver-pl-5">{t.works.transferNFTSuccessful}</p>
                </div>
              </>
            )}
            <div className="ver-mt-5 ver-flex ver-flex-col ver-items-center">
              {!ownerError && !transferInProgress && !transferComplete && (
                <Button
                  disabled={transferProcessStarted}
                  fullWidth
                  onClick={() => {
                    if (!activeWallet) {
                      connect()
                    } else {
                      transferNFT()
                    }
                  }}
                >
                  {t.works.transferButton}
                </Button>
              )}

              <button
                className="ver-flex ver-items-center ver-p-2 ver-underline hover:ver-no-underline ver-font-medium"
                type="button"
                onClick={async () => {
                  await disconnect()
                  resetStates()
                }}
              >
                {t.works.disconnectButton}
              </button>
            </div>
          </div>
        </Dialog>
      )}
    </div>
  )
}

export default TransferNFT
